import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Filter sets the user comes back to, kept in this browser only. Per network,
 * like my-labels: a fee payer address or a fee band means nothing on another
 * chain.
 *
 * `query` is the canonical string from serializeTxFilters, never a parsed
 * object — it is URL-ready for "open in Transactions", and anything stale or
 * malformed in storage is dropped by parseTxFilters when the view is read.
 */
export interface SavedView {
  id: string;
  name: string;
  query: string;
  createdAt: number;
}

interface SavedViewsState {
  views: Record<string, SavedView[]>; // networkId → views, oldest first
  save: (networkId: string, name: string, query: string) => SavedView;
  update: (
    networkId: string,
    id: string,
    patch: Partial<Pick<SavedView, "name" | "query">>,
  ) => void;
  remove: (networkId: string, id: string) => void;
}

/** Short enough to read in a URL; collisions are checked against the network's own views. */
const newId = (taken: SavedView[]): string => {
  for (;;) {
    const id = Math.random().toString(36).slice(2, 10);
    if (!taken.some((v) => v.id === id)) return id;
  }
};

export const useSavedViews = create<SavedViewsState>()(
  persist(
    (set, get) => ({
      views: {},
      save: (networkId, name, query) => {
        const current = get().views[networkId] ?? [];
        const view: SavedView = { id: newId(current), name, query, createdAt: Date.now() };
        set((s) => ({ views: { ...s.views, [networkId]: [...current, view] } }));
        return view;
      },
      update: (networkId, id, patch) =>
        set((s) => ({
          views: {
            ...s.views,
            [networkId]: (s.views[networkId] ?? []).map((v) =>
              v.id === id ? { ...v, ...patch } : v,
            ),
          },
        })),
      remove: (networkId, id) =>
        set((s) => ({
          views: { ...s.views, [networkId]: (s.views[networkId] ?? []).filter((v) => v.id !== id) },
        })),
    }),
    { name: "clustec-saved-views" },
  ),
);

const NO_VIEWS: SavedView[] = [];

/** A network's saved views. The shared empty array keeps the selector's result referentially stable. */
export function useNetworkViews(networkId: string): SavedView[] {
  return useSavedViews((s) => s.views[networkId] ?? NO_VIEWS);
}
